import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PageHeader from '../../components/common/PageHeader';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase/config';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { IoSearchOutline } from 'react-icons/io5';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { IoChevronBack } from 'react-icons/io5';
import { friendlyTimestamp } from '../../utils/dateFormat';

const MessagePage = () => {
  const { currentUser, userData } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => {
    if (!userData?.uid) return;
    loadMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.uid]);

  const loadMessages = async () => {
    try {
      const uid = userData.uid;
      const role = userData?.role || '';

      // adminMessages에서 나에게 온 메시지 조회
      // targetRole 매칭 또는 targetUserIds에 내 uid 포함
      const snap = await getDocs(collection(db, 'adminMessages'));
      const allMessages = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      const myMessages = allMessages.filter(msg => {
        if (msg.targetUserIds?.includes(uid)) return true;
        if (msg.targetRole === 'all') return true;
        if (msg.targetRole === role) return true;
        return false;
      });

      myMessages.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime;
      });

      setMessages(myMessages);
    } catch (err) {
      console.error('메시지 로드 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  const isUnread = (msg) => {
    const uid = userData?.uid;
    if (!uid) return false;
    return !(msg.readBy || {})[uid];
  };

  const unreadCount = messages.filter(msg => isUnread(msg)).length;

  const filtered = messages.filter(msg => {
    if (!searchText) return true;
    const text = searchText.toLowerCase();
    return (msg.title || '').toLowerCase().includes(text) ||
      (msg.content || '').toLowerCase().includes(text);
  });

  const formatTime = (ts) => {
    if (!ts) return '';
    const date = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
    const now = new Date();
    const diff = now - date;
    if (diff < 3600000) return `${Math.floor(diff / 60000)}분 전`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}시간 전`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}일 전`;
    return friendlyTimestamp(ts);
  };

  const markAsRead = async (msg) => {
    const uid = userData?.uid;
    if (!uid) return;
    const readBy = msg.readBy || {};
    if (readBy[uid]) return;
    try {
      await updateDoc(doc(db, 'adminMessages', msg.id), {
        [`readBy.${uid}`]: true,
      });
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, readBy: { ...m.readBy, [uid]: true } } : m));
      window.dispatchEvent(new Event('message-read'));
    } catch (err) {
      console.error('읽음 처리 실패:', err);
    }
  };

  const handleSelectMessage = (msg) => {
    markAsRead(msg);
    setSelectedMessage(msg);
  };

  // Message detail view
  if (selectedMessage) {
    return (
      <Container>
        <DetailHeader>
          <BackBtn onClick={() => setSelectedMessage(null)}><IoChevronBack size={24} /></BackBtn>
          <DetailTitle>메시지 상세</DetailTitle>
        </DetailHeader>
        <DetailContent>
          <DetailSubject>{selectedMessage.title}</DetailSubject>
          <DetailMeta>
            {selectedMessage.authorName && `${selectedMessage.authorName} · `}
            {formatTime(selectedMessage.createdAt)}
          </DetailMeta>
          <DetailBody>{selectedMessage.content}</DetailBody>
        </DetailContent>
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader title={`메시지${unreadCount > 0 ? ` (${unreadCount})` : ''}`} showBack={false} />

      <SearchArea>
        <SearchIcon><IoSearchOutline size={20} /></SearchIcon>
        <SearchInput
          placeholder="메시지 검색..."
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
        />
      </SearchArea>

      <MessageList>
        {loading ? (
          <LoadingSpinner />
        ) : filtered.length === 0 ? (
          <EmptyText>메시지가 없습니다.</EmptyText>
        ) : (
          filtered.map(msg => {
            const unread = isUnread(msg);
            return (
              <MessageItem key={msg.id} onClick={() => handleSelectMessage(msg)} $unread={unread}>
                {unread && <UnreadDot />}
                <Avatar>{(msg.authorName || '관')[0]}</Avatar>
                <MessageContent>
                  <MessageSender>
                    <MessageName $unread={unread}>{msg.authorName || '관리자'}</MessageName>
                    <MessageTime>{formatTime(msg.createdAt)}</MessageTime>
                  </MessageSender>
                  <MessageSubject $unread={unread}>{msg.title}</MessageSubject>
                  <MessageText>{msg.content}</MessageText>
                </MessageContent>
              </MessageItem>
            );
          })
        )}
      </MessageList>
    </Container>
  );
};

export default MessagePage;

const Container = styled.div`
  background: ${({ theme }) => theme.colors.background.default};
  min-height: 100vh;
  padding-bottom: 80px;
`;

const SearchArea = styled.div`
  position: relative;
  padding: 12px 16px;
  background: white;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 28px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.text.hint};
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 12px 12px 40px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  font-size: ${({ theme }) => theme.fontSize.md};
  &::placeholder { color: ${({ theme }) => theme.colors.text.hint}; }
`;

const MessageList = styled.div`
  padding: 0 16px;
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const MessageItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  border: 1px solid ${({ theme }) => theme.colors.border};
  cursor: pointer;
  position: relative;
`;

const UnreadDot = styled.div`
  position: absolute;
  left: 6px;
  top: 50%;
  transform: translateY(-50%);
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #EF4444;
  flex-shrink: 0;
`;

const Avatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: ${({ theme }) => theme.borderRadius.round};
  background: #E5E7EB;
  color: #6B7280;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 600;
  flex-shrink: 0;
`;

const MessageContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const MessageSender = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 2px;
`;

const MessageName = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const MessageTime = styled.span`
  margin-left: auto;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.hint};
`;

const MessageSubject = styled.p`
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ $unread }) => $unread ? 700 : 400};
  margin-bottom: 2px;
`;

const MessageText = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;


const EmptyText = styled.p`
  text-align: center;
  padding: 40px 0;
  color: ${({ theme }) => theme.colors.text.hint};
`;

// Detail view
const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: white;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const BackBtn = styled.button`
  color: ${({ theme }) => theme.colors.text.primary};
`;

const DetailTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.lg};
`;

const DetailContent = styled.div`
  padding: 20px 16px;
`;

const DetailSubject = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xl};
  margin-bottom: 8px;
`;

const DetailMeta = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.hint};
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const DetailBody = styled.p`
  font-size: ${({ theme }) => theme.fontSize.md};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: pre-wrap;
`;
